/* =============================================================================
   UTILS / DATEV-EXPORT — Bestellungen als CSV für die Buchhaltung
   =============================================================================
   Liefert eine vereinfachte DATEV-kompatible CSV. Echtes DATEV-CSV (116 Spalten)
   ist Overkill für ein Burgerladen-MVP. Stattdessen ein klar lesbares Format,
   das jeder Steuerberater per Excel/Numbers öffnen und in DATEV mappen kann:

     Rechnungsnummer; Bestellnummer; Datum; Abholzeit;
     Netto (€); MwSt 7% (€); MwSt 19% (€); Brutto (€);
     Bezahlart; Bezahlt am; Status; Kunde

   Konvention:
     - Semikolon-Separator (deutsche Excel-Default)
     - UTF-8 mit BOM (﻿) damit Excel die Umlaute korrekt rendert
     - Deutsche Zahlenformatierung (Komma als Dezimaltrennzeichen)
     - Datum: TT.MM.JJJJ
     - Nur productive Bestellungen (storno + no_show fließen NICHT ein)

   Wird genutzt vom Tagesabschluss (Tagesexport) und vom Statistik-Sub-Tab
   Bestellungen (Monatsexport).
   ============================================================================ */

import { todayISO, showToast, getRestaurantTimezone, DEFAULT_TIMEZONE } from './helpers.js';

/** "1234" → "12,34" (deutsche Notation, keine 1000er-Trenner damit DATEV nicht stolpert). */
function centsToGermanNumber(cents) {
  const c = Number(cents) || 0;
  const sign = c < 0 ? '-' : '';
  const abs  = Math.abs(c);
  const euros = Math.floor(abs / 100);
  const rest  = abs % 100;
  return sign + String(euros) + ',' + String(rest).padStart(2, '0');
}

/** "2026-05-19" → "19.05.2026". */
function isoToGerman(iso) {
  if (!iso || typeof iso !== 'string' || iso.length < 10) return '';
  return iso.slice(8, 10) + '.' + iso.slice(5, 7) + '.' + iso.slice(0, 4);
}

/** ISO-Timestamp → "19.05.2026 14:32". Leer wenn null.
 *  Phase 28 (S26): TZ-aware — projiziert auf die Restaurant-Timezone
 *  (Default Europe/Berlin), nicht mehr Browser-Local-TZ. */
function tsToGerman(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: getRestaurantTimezone() || DEFAULT_TIMEZONE
  });
}

const STATUS_LABEL = {
  pending:    'eingegangen',
  confirmed:  'bestätigt',
  preparing:  'in Zubereitung',
  ready:      'abholbereit',
  picked_up:  'abgeholt',
  cancelled:  'storniert',
  no_show:    'nicht abgeholt'
};

const METHOD_LABEL = {
  cash:   'Bar',
  card:   'Karte',
  stripe: 'Stripe',
  '':     'unbezahlt'
};

/** CSV-Field escapen: enthält das Feld ", ; oder \n, muss es in "..." stehen,
 *  innere " werden zu "". */
function csvField(s) {
  const v = s == null ? '' : String(s);
  if (/[";\r\n]/.test(v)) {
    return '"' + v.replace(/"/g, '""') + '"';
  }
  return v;
}

const HEADER = [
  'Rechnungsnummer',
  'Bestellnummer',
  'Datum',
  'Abholzeit',
  'Netto (EUR)',
  'MwSt 7% (EUR)',
  'MwSt 19% (EUR)',
  'Brutto (EUR)',
  'Bezahlart',
  'Bezahlt am',
  'Status',
  'Kunde'
];

/** Zieht productive orders zwischen from..to (inkl.) FRESH AUS DER DB.
 *
 *  Session 16: vorher wurde der lokale Cache _state.orders gelesen, der bei
 *  größeren Zeiträumen unvollständig oder veraltet sein kann (gerade nach
 *  Tab-Wechsel + Realtime-Lücken). Das führte zu „MwSt-Spalten=0" und
 *  fehlenden Bestellungen im Monatsexport. Seither nutzen wir denselben
 *  Range-Helper wie der DSFinV-K-Export — garantiert vollständige + aktuelle
 *  Daten direkt vom Supabase Postgres.
 *
 *  Phase 10a.2: Korrekturbelege (is_correction=true) gehören in DATEV
 *  rein — sie haben status='cancelled', aber tragen mit negativen Beträgen
 *  zur Buchhaltung bei. Echte Stornos vor Bezahlung (cancelled ohne
 *  is_correction) bleiben außen vor. */
/** S137 — ein Teilbeleg (order_receipts) als DATEV-zeilenfähiges Beleg-Objekt
 *  (order-shaped, damit `buildRow` ihn 1:1 nimmt). Datum/Kunde aus der Eltern-
 *  Order, Beträge/Zahlart/Rechnungsnr. aus dem Teilbeleg, Bezahlt-am = created_at. */
function receiptToDatevRow(r, parent) {
  return {
    invoiceNumber:    r.invoiceNumber,
    orderNumber:      parent ? parent.orderNumber : r.orderId,
    pickupDate:       parent ? parent.pickupDate : (r.createdAt || '').slice(0, 10),
    pickupTime:       parent ? parent.pickupTime : '',
    subtotalNetCents: r.subtotalNetCents,
    tax7Cents:        r.tax7AmountCents,
    tax19Cents:       r.tax19AmountCents,
    totalGrossCents:  r.totalGrossCents,
    paymentMethod:    r.paymentMethod,
    paymentStatus:    'paid',
    paidAt:           r.createdAt,
    status:           parent ? parent.status : 'picked_up',
    customerName:     parent ? parent.customerName : ''
  };
}

/** S242/phase_173 — Teil-Storno-Beleg als DATEV-Zeile. Beträge liegen bereits
 *  negativ in der DB (umsatzmindernd), die Zahlart ist der Erstattungsweg. */
function correctionBatchToDatevRow(b, parent) {
  return {
    invoiceNumber:    b.invoiceNumber,
    orderNumber:      parent ? parent.orderNumber + '-PK' : b.orderId,
    pickupDate:       parent ? parent.pickupDate : String(b.createdAt || '').slice(0, 10),
    pickupTime:       parent ? parent.pickupTime : '',
    subtotalNetCents: b.subtotalNetCents,
    tax7Cents:        b.tax7Cents,
    tax19Cents:       b.tax19Cents,
    totalGrossCents:  b.totalGrossCents,
    paymentMethod:    b.paymentMethod,
    paymentStatus:    'refunded',
    paidAt:           b.createdAt,
    status:           parent ? parent.status : 'picked_up',
    customerName:     parent ? parent.customerName : ''
  };
}

/** S137 — fiskalische Belege für DATEV FRESH AUS DER DB.
 *
 *  Seit P4 ist die Buchhaltungs-Quelle BELEG-basiert (nicht order-basiert):
 *    • Voll-Zahlung: orders-Zeile MIT invoice_number
 *    • Teilbeleg:    order_receipts-Zeile (Split — eigene invoice_number + Zahlart)
 *    • Storno:       Korrektur-Order (is_correction, negative Beträge)
 *  Offene/unbezahlte Orders, Schwund-Orders und die Split-Mutter-Order (alle ohne
 *  invoice_number) fallen strukturell raus — „ohne Beleg kein Buchungssatz". Damit
 *  erscheint ein Split nicht mehr als EINE Zahlart-Zeile, sondern als zwei Teilbelege
 *  mit je eigener Zahlart; eine Schwund-Order taucht gar nicht als „unbezahlt" auf. */
async function selectOrders(fromIso, toIso) {
  const storage = window.App && window.App.storage;
  if (!storage || !storage.orders || typeof storage.orders.listForRange !== 'function') {
    throw new Error('storage.orders.listForRange fehlt — alten Storage-Adapter erkannt.');
  }
  const orders = await storage.orders.listForRange(fromIso, toIso);
  let receipts = [];
  if (typeof storage.orders.listReceiptsForRange === 'function') {
    try {
      receipts = await storage.orders.listReceiptsForRange(fromIso, toIso);
    } catch (e) {
      console.warn('[datev] order_receipts nicht lesbar:', e.message || e);
    }
  }
  const ordersById = {};
  for (const o of (orders || [])) ordersById[o.id] = o;

  const belege = [];
  for (const o of (orders || [])) {
    if (o.isCorrection) { belege.push(o); continue; }       // Stornobelege immer rein (negativ)
    if (!o.invoiceNumber) continue;                          // offen/Schwund/Split-Mutter → raus
    if (o.status === 'cancelled' || o.status === 'no_show') continue;
    belege.push(o);
  }
  for (const r of (receipts || [])) belege.push(receiptToDatevRow(r, ordersById[r.orderId]));

  // S242/phase_173 — Teil-Storno-Belege. Sie fehlten bis S241 komplett in der
  // Buchhaltung: der Export kannte nur Korrektur-ORDERS (is_correction).
  if (typeof storage.orders.listCorrectionBatchesForRange === 'function') {
    try {
      const batches = await storage.orders.listCorrectionBatchesForRange(fromIso, toIso);
      for (const b of (batches || [])) {
        belege.push(correctionBatchToDatevRow(b, ordersById[b.orderId]));
      }
    } catch (e) {
      console.warn('[datev] order_correction_batches nicht lesbar:', e.message || e);
    }
  }

  return belege.sort((a, b) => {
    // Primär nach Datum + Abholzeit, sekundär nach Rechnungs-/Bestellnummer
    const da = (a.pickupDate || '') + (a.pickupTime || '');
    const db = (b.pickupDate || '') + (b.pickupTime || '');
    if (da !== db) return da.localeCompare(db);
    return ((a.invoiceNumber || a.orderNumber || '')).localeCompare(b.invoiceNumber || b.orderNumber || '');
  });
}

function buildRow(o) {
  // 'paid' = positive Buchung, 'refunded' = Korrekturbeleg (Geld zurück auf
  // dieselbe Bezahlart). Beide tragen die Bezahlart in der Spalte; nur
  // wirklich unbezahlte Bestellungen bleiben leer.
  const isSettled = o.paymentStatus === 'paid' || o.paymentStatus === 'refunded';
  const method = isSettled ? (o.paymentMethod || '') : '';
  return [
    csvField(o.invoiceNumber || ''),
    csvField(o.orderNumber   || ''),
    csvField(isoToGerman(o.pickupDate)),
    csvField((o.pickupTime || '').slice(0, 5)),
    csvField(centsToGermanNumber(o.subtotalNetCents)),
    csvField(centsToGermanNumber(o.tax7Cents  != null ? o.tax7Cents  : o.tax7AmountCents)),
    csvField(centsToGermanNumber(o.tax19Cents != null ? o.tax19Cents : o.tax19AmountCents)),
    csvField(centsToGermanNumber(o.totalGrossCents)),
    csvField(METHOD_LABEL[method] != null ? METHOD_LABEL[method] : method),
    csvField(tsToGerman(o.paidAt)),
    csvField(STATUS_LABEL[o.status] || o.status || ''),
    csvField(o.customerName || '')
  ].join(';');
}

/**
 * Hauptexport-Funktion. opts = { from, to, label? }
 * - from/to als ISO-Date-Strings (yyyy-mm-dd), inklusiv.
 * - label optional als Dateinamen-Suffix (z.B. 'tagesabschluss', 'monat-mai').
 * Falls keine Bestellungen im Zeitraum: Toast statt leerer Datei.
 *
 * Async seit Session 16 — selectOrders zieht jetzt frisch aus der DB.
 */
export async function exportOrdersDatevCsv(opts) {
  const o = opts || {};
  const today = todayISO();
  const from  = o.from || today;
  const to    = o.to   || from;
  const label = o.label || (from === to ? 'tag-' + from : 'zeitraum-' + from + '_' + to);

  let orders;
  try {
    showToast('DATEV: lade Daten…', 'info');
    orders = await selectOrders(from, to);
  } catch (e) {
    console.error('[exportOrdersDatevCsv] selectOrders:', e);
    showToast('DATEV-Export fehlgeschlagen: ' + (e.message || e), 'error');
    return { rows: 0, filename: null };
  }

  if (orders.length === 0) {
    showToast('Keine productive Bestellungen im Zeitraum — kein CSV erzeugt.', 'info');
    return { rows: 0, filename: null };
  }

  const lines = [
    HEADER.map(csvField).join(';'),
    ...orders.map(buildRow)
  ];
  // CRLF für maximale Excel-Kompatibilität
  const content = '﻿' + lines.join('\r\n') + '\r\n';

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `gg-bestellungen-${label}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  showToast(`${orders.length} Bestellung${orders.length === 1 ? '' : 'en'} als CSV exportiert.`, 'success');
  return { rows: orders.length, filename: a.download };
}

/** Komfort-Wrapper für den Statistik-Sub-Tab «Bestellungen» (Monatsexport).
 *  Phase 28 (S26): keine Local→UTC-Falle mehr — ISO direkt konstruieren. */
export async function exportOrdersDatevCsvForMonth(year, month) {
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const mm = String(month + 1).padStart(2, '0');
  const fromIso = `${year}-${mm}-01`;
  const toIso   = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
  const monthLabels = ['januar','februar','maerz','april','mai','juni','juli','august','september','oktober','november','dezember'];
  const label = 'monat-' + monthLabels[month] + '-' + year;
  return exportOrdersDatevCsv({ from: fromIso, to: toIso, label });
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    exportOrdersDatevCsv,
    exportOrdersDatevCsvForMonth
  });
}

/* =============================================================================
   Test-Hooks (Phase 27.3, Session 31)
   =============================================================================
   Additive named exports der Pure-Function-Helpers, damit die Test-Suite die
   GoBD-kritische Konvertierungs-Logik isoliert prüfen kann — Vorzeichen-
   Stabilität bei Stornobelegen, deutsche Komma-Notation, CRLF, ISO→DE-Datum,
   CSV-Field-Escaping. Memory-Lesson `feedback_gg_hard_way`: lieber additive
   Test-Exports als Reflection-Tricks im Test.
============================================================================ */

export {
  centsToGermanNumber,
  isoToGerman,
  tsToGerman,
  csvField,
  buildRow,
  HEADER,
  STATUS_LABEL,
  METHOD_LABEL
};
